import React from 'react';
import { FileText, MessageCircle, Award, Plus, TrendingUp } from 'lucide-react';
import type { TeamActivity } from '../../types/collaboration';

interface TeamActivityFeedProps {
  activities: TeamActivity[];
  limit?: number;
}

export const TeamActivityFeed: React.FC<TeamActivityFeedProps> = ({ activities, limit }) => {
  const displayActivities = limit ? activities.slice(0, limit) : activities;

  const getIcon = (type: TeamActivity['type']) => {
    switch (type) {
      case 'story-published':
        return <FileText className="h-4 w-4 text-primary-600" />;
      case 'resource-added':
        return <Plus className="h-4 w-4 text-sage-600" />;
      case 'comment-posted':
        return <MessageCircle className="h-4 w-4 text-[var(--mustard)]" />;
      case 'achievement-unlocked':
        return <Award className="h-4 w-4 text-coral-600" />;
      case 'outcome-shared':
        return <TrendingUp className="h-4 w-4 text-primary-600" />;
    }
  };

  const getIconBg = (type: TeamActivity['type']) => {
    switch (type) {
      case 'story-published':
        return 'bg-primary-100';
      case 'resource-added':
        return 'bg-sage-100';
      case 'comment-posted':
        return 'bg-[var(--mustard-dim)]';
      case 'achievement-unlocked':
        return 'bg-coral-100';
      case 'outcome-shared':
        return 'bg-primary-100';
    }
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now.getTime() - then.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  return (
    <div className="space-y-3">
      {displayActivities.map((activity) => (
        <div
          key={activity.id}
          className="flex gap-3 p-3 bg-[var(--surface)] hover:bg-[var(--hover-tint)] transition-colors"
        >
          <div className={`flex-shrink-0 w-10 h-10 rounded-full ${getIconBg(activity.type)} flex items-center justify-center`}>
            {getIcon(activity.type)}
          </div>
          
          <div className="flex-1 min-w-0">
            <p className="text-sm text-charcoal-900">
              <span className="font-semibold">{activity.actor.name}</span>
              {' '}
              <span className="text-charcoal-600">{activity.title}</span>
            </p>
            <p className="text-sm text-charcoal-700 mt-1">{activity.description}</p>
            <p className="text-xs text-charcoal-500 mt-1">{getTimeAgo(activity.timestamp)}</p>
          </div>
        </div>
      ))}
      
      {displayActivities.length === 0 && (
        <div className="text-center py-8 text-charcoal-500">
          <p>No recent team activity</p>
        </div>
      )}
    </div>
  );
};

